import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { SubscriptionSummary } from '../types/subscription';
import {
  selectDemoEndTimestamp,
  selectDemoStartTimestamp,
  selectIsDemo,
} from '../store/slices/demo';
import { useDemoRebasedSubscriptions } from '../store/hooks/useDemoRebasedSubscriptions';

interface Inputs {
  /** Selected (raw) active subscription from the store. May be null. */
  rawActive: SubscriptionSummary | null;
  /** Raw summary list. Used unmodified outside demo mode. */
  rawSummaries: SubscriptionSummary[];
}

interface Result {
  /** Subscription list to drive the sidebar / cluster grouping. */
  subscriptionSummaries: SubscriptionSummary[];
  /** Active subscription with the demo rebase + window-clip applied. */
  activeSubscription: SubscriptionSummary | null;
}

/**
 * Builds the windowed view of the active subscription + the sidebar list for
 * the Subscription detail page.
 *
 *   - Outside demo mode the inputs are returned untouched.
 *   - Inside demo mode the sidebar list is replaced by the rebased + excluded
 *     subset (so excluded subs don't appear), the active sub is merged with
 *     its rebased twin (totals/profits reflect the window), and its lifetime
 *     `transactions` / `compactTransactions` / `chartData.dataPoints.*` arrays
 *     are clipped to the date window so the detail tabs show the same window
 *     the rest of the dashboard uses.
 */
const useDemoClippedActiveSubscription = ({
  rawActive,
  rawSummaries,
}: Inputs): Result => {
  const isDemo = useSelector(selectIsDemo);
  const { visible: rebasedVisible, byBaseAsset: rebasedByBase } =
    useDemoRebasedSubscriptions();
  const demoStart = useSelector(selectDemoStartTimestamp);
  const demoEnd = useSelector(selectDemoEndTimestamp);

  const subscriptionSummaries = isDemo ? rebasedVisible : rawSummaries;

  const activeSubscription = useMemo<SubscriptionSummary | null>(() => {
    if (!rawActive) return rawActive;
    if (!isDemo) return rawActive;
    const rebased = rebasedByBase[rawActive.baseAsset];
    const merged: SubscriptionSummary = rebased
      ? { ...rawActive, ...rebased }
      : { ...rawActive };

    if (demoStart == null && demoEnd == null) return merged;

    const inWindow = (ts: number) =>
      (demoStart == null || ts >= demoStart) &&
      (demoEnd == null || ts <= demoEnd);
    const tsOf = (t: any): number =>
      t?.transactionTimestamp ?? t?.timestamp ?? 0;

    if (Array.isArray(merged.transactions)) {
      merged.transactions = merged.transactions.filter((t) =>
        inWindow(tsOf(t)),
      );
    }
    if (Array.isArray(merged.compactTransactions)) {
      merged.compactTransactions = merged.compactTransactions.filter((t) =>
        inWindow(tsOf(t)),
      );
    }
    if (merged.chartData?.dataPoints) {
      const dp: any = merged.chartData.dataPoints;
      const clipArr = (arr: any[] | undefined) =>
        Array.isArray(arr) ? arr.filter((p) => inWindow(p.timestamp)) : arr;
      merged.chartData = {
        ...merged.chartData,
        dataPoints: {
          ...dp,
          total: {
            stable: clipArr(dp.total?.stable) ?? [],
            asset: clipArr(dp.total?.asset),
          },
          noAlgorithmicProgression: clipArr(dp.noAlgorithmicProgression) ?? [],
          stable: dp.stable
            ? {
                all: clipArr(dp.stable.all) ?? [],
                accumulative: clipArr(dp.stable.accumulative) ?? [],
              }
            : undefined,
          asset: dp.asset
            ? {
                all: clipArr(dp.asset.all) ?? [],
                accumulative: clipArr(dp.asset.accumulative) ?? [],
              }
            : undefined,
          compactTransactions: clipArr(dp.compactTransactions),
        },
      };
    }
    return merged;
  }, [rawActive, isDemo, rebasedByBase, demoStart, demoEnd]);

  return { subscriptionSummaries, activeSubscription };
};

export default useDemoClippedActiveSubscription;
